import { useState, useEffect, useRef, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Input from "../input/Input";
import Message from "../message/Message";
import Spinner from "../spinner/Spinner";
import ProductListStyles from "./ProductListStyles.module.css";
import {
  CreateProductModalProps,
  AddOnProps,
} from "../../types/ComponentProps";
import ToggleSwitch from "../toggleSwitch/ToggleSwitch";
import { categoryOptions } from "./dropdown";
import useGetAllAddOns from "../../hooks/useGetAllAddOns";
import CustomSelectAddOns from "../customSelectAddOns/CustomSelectAddOns";
import CustomSelectProduct from "../customSelectProduct/CustomSelectProduct";
import MerchVariant from "./MerchVariant";

const CreateProductModal = ({
  createProductSubmitHandler,
  onCloseClickHandler,
  inputChangeHandler,
  createProductForm,
  createProductIsLoading,
  errorHandler,
  setCreateProductForm,
  toggleState,
  setToggleState,
}: CreateProductModalProps) => {
  const [preview, setPreview] = useState<string | null>(null);
  const { getAllAddOnsData } = useGetAllAddOns();
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  // Handle Escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !createProductIsLoading) {
        onCloseClickHandler();
      }
    },
    [onCloseClickHandler, createProductIsLoading]
  );

  // Focus trap within modal
  const handleFocusTrap = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab") return;

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex="0"]',
      'a[href]'
    ].join(', ');

    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(focusableSelectors);
    
    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keydown", handleFocusTrap);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keydown", handleFocusTrap);
    };
  }, [handleKeyDown, handleFocusTrap]);

  // Focus close button only on initial mount
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []); // Empty dependency array = only on mount

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [], "image/png": [] },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setPreview(URL.createObjectURL(file));
        inputChangeHandler({ target: { name: "productImage", value: file } });
      }
    },
  });

  const handleSelectTypeAndCategory =
    (field: "type" | "category") =>
    (selectedOption: { value: string; label: string }) => {
      setCreateProductForm((prevForm: any) => {
        const updatedForm = {
          ...prevForm,
          [field]: selectedOption.value,
        };

        if (field === "type" && selectedOption.value === "Voucher") {
          setToggleState(false);
          updatedForm.addOns = [];
        }

        return updatedForm;
      });
    };

  const handleSelectAddOns = (selectedOption: AddOnProps) => {
    setCreateProductForm((prevForm: any) => {
      const addOnsArray = Array.isArray(prevForm.addOns) ? prevForm.addOns : [];

      const isDuplicate = addOnsArray.some(
        (addOn: AddOnProps) => addOn.addonId === selectedOption.addonId
      );

      if (isDuplicate) return prevForm;

      const updatedForm = {
        ...prevForm,
        addOns: [...addOnsArray, selectedOption],
      };

      return updatedForm;
    });
  };

  const removeListAddOns = (addonId: number) => {
    setCreateProductForm((prevForm: any) => {
      const updatedAddOns =
        prevForm.addOns?.filter(
          (addOn: AddOnProps) => addOn.addonId !== addonId
        ) || [];

      if (updatedAddOns.length === 0) {
        setToggleState(false);
      }

      return {
        ...prevForm,
        addOns: updatedAddOns,
      };
    });
  };

  const filteredAddOns =
    getAllAddOnsData?.pages
      .flatMap((page) => page?.addons)
      .filter(
        (addOn: AddOnProps) =>
          !createProductForm?.addOns.some(
            (selectedAddOn: AddOnProps) =>
              selectedAddOn?.addonId === addOn?.addonId
          )
      ) || [];

  return (
    <div 
      className={ProductListStyles.createProductModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-product-modal-title"
    >
      <div 
        className={ProductListStyles.createProductModalMainContent}
        ref={modalRef}
      >
        <div className={ProductListStyles.flexBoxBlk}>
          <div>
            <h1 id="create-product-modal-title">
              {createProductForm?.type === "Merch" && "Add New Merch Product"}
              {createProductForm?.type === "Voucher" && "Add New Voucher"}
            </h1>
          </div>
          <div>
            <button
              ref={closeButtonRef}
              className={`fa-solid fa-xmark ${ProductListStyles.closeIconCreateBadge}`}
              onClick={onCloseClickHandler}
              disabled={createProductIsLoading}
              aria-label="Close modal"
            ></button>
          </div>
        </div>

        <hr className={ProductListStyles.horizontalLine} />
        <div className={ProductListStyles.scrollContainer}>
          <div className={ProductListStyles.formSection}>
            <h2 style={{ marginTop: "20px" }} id="dropzone-label">Image</h2>
            <div 
              {...getRootProps()} 
              className={ProductListStyles.dropzone}
              role="button"
              aria-labelledby="dropzone-label"
              aria-describedby="dropzone-instructions"
            >
              <input {...getInputProps()} aria-label="Upload product image" />
              {preview ? (
                <div className={ProductListStyles.previewImageBlk}>
                  <img
                    src={preview}
                    alt="Product image preview"
                    className={ProductListStyles.imagePreview}
                  />
                </div>
              ) : (
                <div className={ProductListStyles.dragBoxBlk}>
                  <p id="dropzone-instructions">Choose a file or drag it here. Accepts JPEG and PNG images.</p>
                </div>
              )}
            </div>
            <h2>Basic Details</h2>

            {createProductForm?.type === "Merch" && (
              <div className={ProductListStyles.merchBlkContainer}>
                <div className={ProductListStyles.inputFlexBox}>
                  <div className={ProductListStyles.dropDownFlexContent}>
                    <Input
                      type="text"
                      label="Product Name"
                      onChangeHandler={inputChangeHandler}
                      name="productName"
                      value={createProductForm?.productName}
                      maxLength={150}
                      aria-describedby="productName-hint"
                    />
                    <div>
                      <p id="productName-hint">Limited to 150 characters</p>
                    </div>
                  </div>
                  <div className={ProductListStyles.dropDownFlexContent}>
                    <label id="category-label">Category</label>
                    <CustomSelectProduct
                      options={categoryOptions}
                      onSelect={handleSelectTypeAndCategory("category")}
                      placeholder="Select category"
                      id="category-select"
                    />
                  </div>
                </div>
                <div className={ProductListStyles.inputFlexBox}>
                  <div>
                    <Input
                      type="number"
                      label="Price"
                      onChangeHandler={inputChangeHandler}
                      name="price"
                      maxLength={6}
                      value={
                        createProductForm?.price
                          ? createProductForm.price.toString()
                          : ""
                      }
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      label="Quantity"
                      onChangeHandler={inputChangeHandler}
                      name="quantity"
                      maxLength={6}
                      value={
                        createProductForm?.quantity !== null && createProductForm?.quantity !== undefined
                          ? createProductForm.quantity.toString()
                          : ""
                      }
                    />
                  </div>
                </div>
                <div className={ProductListStyles.addOnSection}>
                  <div className={ProductListStyles.flexBoxBlk}>
                    <div>
                      <h1 id="addons-toggle-label">Add ons</h1>
                    </div>
                    <div>
                      <ToggleSwitch
                        toggleState={toggleState}
                        setToggleState={setToggleState}
                        addOns={createProductForm?.addOns}
                        label="Enable add-ons"
                        id="addons-toggle"
                      />
                    </div>
                  </div>
                  {toggleState && (
                    <>
                      <CustomSelectAddOns
                        options={filteredAddOns}
                        onSelect={handleSelectAddOns}
                        setCreateProductForm={setCreateProductForm}
                        placeholder="Select Add-Ons"
                        id="addons-select"
                      />
                      {createProductForm?.addOns.length === 0 ? null : (
                        <div 
                          className={ProductListStyles.addOnFlex}
                          role="list"
                          aria-label="Selected add-ons"
                        >
                          {createProductForm?.addOns.map(
                            (data: AddOnProps, index: number) => {
                              return (
                                <div
                                  key={index}
                                  className={ProductListStyles.addOn}
                                  role="listitem"
                                >
                                  <span>{data?.addonName}</span>
                                  <button
                                    type="button"
                                    className={ProductListStyles.addOnRemoveButton}
                                    onClick={() =>
                                      removeListAddOns(Number(data?.addonId))
                                    }
                                    aria-label={`Remove ${data?.addonName} add-on`}
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            }
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <Input
                  type="text"
                  label="Description"
                  onChangeHandler={inputChangeHandler}
                  name="productDescription"
                  value={createProductForm?.productDescription}
                  maxLength={150}
                  aria-describedby="description-hint"
                />
                <div>
                  <p id="description-hint">Limited to 150 characters</p>
                </div>
                <hr
                  className={ProductListStyles.horizontalLine}
                  style={{ marginTop: "30px", marginBottom: "20px" }}
                />
                <MerchVariant
                  createProductForm={createProductForm}
                  setCreateProductForm={setCreateProductForm}
                />
                <p className={ProductListStyles.variantQuantityNote}>
                  Variants are optional. If you add size variants, stock will be managed per size. Otherwise, the Quantity field above will be used.
                </p>
              </div>
            )}

            {createProductForm?.type === "Voucher" && (
              <div className={ProductListStyles.voucherBlkContainer}>
                <div className={ProductListStyles.inputFlexBox}>
                  <div>
                    <Input
                      type="text"
                      label="Product Name"
                      onChangeHandler={inputChangeHandler}
                      name="productName"
                      value={createProductForm?.productName}
                    />
                  </div>

                  <div>
                    <Input
                      type="number"
                      label="Price"
                      onChangeHandler={inputChangeHandler}
                      name="price"
                      maxLength={6}
                      value={
                        createProductForm?.price
                          ? createProductForm.price.toString()
                          : ""
                      }
                    />
                  </div>
                </div>
                <div className={ProductListStyles.inputFlexBox}>
                  <div>
                    <Input
                      type="number"
                      label="Quantity"
                      onChangeHandler={inputChangeHandler}
                      name="quantity"
                      maxLength={6}
                      value={
                        createProductForm?.quantity !== null
                          ? createProductForm.quantity.toString()
                          : ""
                      }
                    />
                  </div>
                  <div>
                    <div className={ProductListStyles.dropDownFlexContent}>
                      <label id="voucher-category-label">Category</label>
                      <CustomSelectProduct
                        options={categoryOptions}
                        onSelect={handleSelectTypeAndCategory("category")}
                        placeholder="Select category"
                        id="voucher-category-select"
                      />
                    </div>
                  </div>
                </div>
                <Input
                  type="text"
                  label="Description"
                  onChangeHandler={inputChangeHandler}
                  name="productDescription"
                  value={createProductForm?.productDescription}
                />
              </div>
            )}
          </div>

          <hr
            className={ProductListStyles.horizontalLine}
            style={{ marginTop: "20px", marginBottom: "20px" }}
          />
          {!errorHandler.success && errorHandler?.message ? (
            <Message variant="error">{errorHandler.message}</Message>
          ) : null}
          <button
            ref={submitButtonRef}
            className={ProductListStyles.buttonCreateProduct}
            onClick={createProductSubmitHandler}
            disabled={createProductIsLoading}
          >
            {createProductIsLoading ? (
              <Spinner variant="small-spinner" alignSpin="center-spinner" />
            ) : (
              <>
                Add Product<i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProductModal;
